import { Check, X } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { VIEWER_OVERLAY_CLASSNAME } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import { PackChatReplyForm } from "@/payroll/pack-chat/pack-chat-reply-form";
import {
  CellCommentThreadView,
  isInsideCellCommentOverlay,
} from "./cell-comment-thread";
import {
  useReplyToCellCommentThread,
  useStartCellCommentThread,
} from "./query";
import type { ICellCommentTarget, ICellCommentThread } from "./types";
import { useCellCommentActions } from "./use-cell-comment-actions";

export interface CellCommentCardTarget extends ICellCommentTarget {
  // The cell's current grid (display) position, to anchor the card beside it.
  gridCol: number;
  gridRow: number;
  // Label for the card header, e.g. "D12".
  address: string;
  context?: ICellCommentThread["context"];
  // Focus the composer on open: yes when the user asked to comment, no when
  // the card opened because the cursor landed on a commented cell — keyboard
  // navigation must stay on the grid.
  focusComposer: boolean;
}

/**
 * The comment card for one cell: its threads, a reply box on the open one, or a
 * box to start a thread when none is open. Resolving sits in the header beside
 * close, since it closes the conversation the same way. Anchored at a screen
 * point, like the grid's context menu, so it floats over the canvas next to the
 * cell.
 */
export const CellCommentCard = ({
  businessId,
  runId,
  target,
  anchor,
  threads,
  onClose,
}: {
  businessId: string | null;
  runId: string;
  target: CellCommentCardTarget | null;
  // Screen point beside the cell, or null while the cell is scrolled out of
  // view (the card hides and comes back with it).
  anchor: { x: number; y: number } | null;
  threads: ICellCommentThread[];
  onClose: () => void;
}) => {
  const start = useStartCellCommentThread(businessId, runId);
  const reply = useReplyToCellCommentThread(businessId, runId);
  const actions = useCellCommentActions(businessId, runId);
  const openThread = threads.find((thread) => !thread.resolved_at);

  return (
    <Popover
      open={target !== null && anchor !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={false}
    >
      <PopoverAnchor asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed h-0 w-0"
          style={{ left: anchor?.x ?? 0, top: anchor?.y ?? 0 }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="right"
        className={cn(VIEWER_OVERLAY_CLASSNAME, "w-80 p-0")}
        // The anchor follows the cell as the grid scrolls.
        updatePositionStrategy="always"
        // The grid refocuses its canvas on click; that must not close the card.
        onFocusOutside={(event) => event.preventDefault()}
        // A comment's "more" menu is portaled outside the card.
        onInteractOutside={(event) => {
          if (isInsideCellCommentOverlay(event.target)) event.preventDefault();
        }}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={target ? `Comments on ${target.address}` : "Comments"}
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          fullWidth
          className="border-b border-stroke-tertiary py-2 pl-4 pr-2"
        >
          <Typography variant="subtitleW3" className="min-w-0 truncate">
            Comments{target ? ` · ${target.address}` : ""}
          </Typography>
          <Stack direction="row" align="center" gap="xs" className="shrink-0">
            {openThread && (
              <Button
                variant="text"
                size="xs"
                className="gap-1 px-2 [&_svg]:size-3.5"
                disabled={actions.isUpdating}
                onClick={() => void actions.resolve(openThread.id)}
              >
                <Check aria-hidden />
                Resolve
              </Button>
            )}
            <button
              type="button"
              aria-label="Close comments"
              className="flex size-7 items-center justify-center rounded-md text-tertiary-text hover:bg-bg-secondary hover:text-primary-text"
              onClick={onClose}
            >
              <X className="size-4" aria-hidden />
            </button>
          </Stack>
        </Stack>
        <Stack
          gap="md"
          align="stretch"
          fullWidth
          className="max-h-96 overflow-y-auto px-4 py-3"
        >
          {threads.map((thread) => (
            <CellCommentThreadView
              key={thread.id}
              thread={thread}
              isUpdating={actions.isUpdating}
              onDeleteComment={(commentId) =>
                void actions.deleteComment(thread.id, commentId)
              }
              onReopen={() => void actions.reopen(thread.id)}
            />
          ))}
          {target &&
            (openThread ? (
              <PackChatReplyForm
                key={openThread.id}
                autoFocus={target.focusComposer}
                loading={reply.isPending}
                onReply={async (body) => {
                  await reply.mutateAsync({ threadId: openThread.id, body });
                }}
              />
            ) : (
              <PackChatReplyForm
                key={`${target.sheetId}:${target.row}:${target.col}`}
                autoFocus={target.focusComposer}
                loading={start.isPending}
                placeholder="Add a comment"
                actionLabel="Comment"
                onReply={async (body) => {
                  await start.mutateAsync({
                    target,
                    body,
                    context: target.context,
                  });
                }}
              />
            ))}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
